import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import classNames from 'classnames';
import get from 'lodash-es/get';
import isArray from 'lodash-es/isArray';
import isObject from 'lodash-es/isObject';
import isString from 'lodash-es/isString';
import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { PropTypes as PropTypes$1 } from '@panneau/core';
import { useDisplaysComponents } from '@panneau/core/contexts';
import { selectItem, selectPage, getComponentFromName } from '@panneau/core/utils';
import Empty from '@panneau/element-empty';
import Loading from '@panneau/element-loading';
import _objectSpread from '@babel/runtime/helpers/objectSpread2';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import omit from 'lodash-es/omit';
import queryString from 'query-string';
import Icon from '@panneau/element-icon';
import Link from '@panneau/element-link';

var _excluded$1 = ["baseUrl", "query", "field", "parameterName", "directionParameterName", "directions", "keepsPage", "children", "onQueryChange", "onClick"];
var propTypes$1 = {
  baseUrl: PropTypes.string,
  query: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  field: PropTypes.oneOfType([PropTypes.string, PropTypes$1.field]),
  children: PropTypes.node,
  parameterName: PropTypes.string,
  directionParameterName: PropTypes.string,
  directions: PropTypes.arrayOf(PropTypes.string),
  keepsPage: PropTypes.bool,
  onQueryChange: PropTypes.func,
  onClick: PropTypes.func
};
var defaultProps$1 = {
  baseUrl: '',
  query: null,
  field: null,
  children: null,
  parameterName: 'order',
  directionParameterName: 'order_direction',
  directions: ['asc', 'desc', null],
  keepsPage: false,
  onQueryChange: null,
  onClick: null
};
var SortLink = function SortLink(_ref) {
  var baseUrl = _ref.baseUrl,
    query = _ref.query,
    field = _ref.field,
    parameterName = _ref.parameterName,
    directionParameterName = _ref.directionParameterName,
    directions = _ref.directions,
    keepsPage = _ref.keepsPage,
    children = _ref.children,
    onQueryChange = _ref.onQueryChange,
    customOnClick = _ref.onClick,
    props = _objectWithoutProperties(_ref, _excluded$1);
  var _ref2 = isObject(field) ? field : {
      name: field
    },
    columnName = _ref2.name;
  var _ref3 = query || {},
    _ref3$parameterName = _ref3[parameterName],
    currentSortValue = _ref3$parameterName === void 0 ? null : _ref3$parameterName;
  var _split = (currentSortValue || '').split(','),
    _split2 = _slicedToArray(_split, 2),
    currentSort = _split2[0],
    _split2$ = _split2[1],
    fallbackCurrentDirection = _split2$ === void 0 ? null : _split2$;
  var currentSortDirection = directionParameterName !== null ? (query || {})[directionParameterName] || null : fallbackCurrentDirection;
  var sortDirectionIndex = directions.indexOf(currentSortDirection);
  var newSortDirection = directions[sortDirectionIndex === directions.length - 1 ? 0 : sortDirectionIndex + 1];
  var newQuery = useMemo(function () {
    var queryWithoutSort = omit(query || {}, [parameterName, directionParameterName, !keepsPage ? 'page' : null].filter(function (it) {
      return it !== null;
    }));
    if (newSortDirection === null && Object.keys(queryWithoutSort).length === 0) {
      return null;
    }
    var sortQuery = directionParameterName !== null ? _defineProperty(_defineProperty({}, parameterName, columnName), directionParameterName, newSortDirection) : _defineProperty({}, parameterName, [columnName, newSortDirection].filter(function (it) {
      return it !== null;
    }).join(','));
    return newSortDirection !== null ? _objectSpread(_objectSpread({}, queryWithoutSort), sortQuery) : queryWithoutSort;
  }, [query, parameterName, columnName, directionParameterName, newSortDirection]);
  var onClick = useCallback(function (e) {
    if (onQueryChange !== null) {
      e.preventDefault();
      onQueryChange(newQuery);
    }
    if (customOnClick !== null) {
      customOnClick(e);
    }
  }, [newQuery, onQueryChange, customOnClick]);
  return /*#__PURE__*/React.createElement(Link, Object.assign({
    href: "".concat(baseUrl || '').concat(newQuery !== null ? "?".concat(queryString.stringify(newQuery)) : ''),
    onClick: onClick
  }, props), children, currentSort === columnName && currentSortDirection !== null ? /*#__PURE__*/React.createElement(Icon, {
    name: currentSortDirection === 'desc' ? 'caret-down-fill' : 'caret-up-fill',
    className: "ms-1"
  }) : null);
};
SortLink.propTypes = propTypes$1;
SortLink.defaultProps = defaultProps$1;

var _excluded = ["id", "component", "field", "path", "columnClassName"];
var propTypes = {
  items: PropTypes$1.items,
  columns: PropTypes$1.tableColumns,
  loading: PropTypes.bool,
  loaded: PropTypes.bool,
  empty: PropTypes.bool,
  theme: PropTypes.string,
  baseUrl: PropTypes.string,
  query: PropTypes.object,
  // eslint-disable-line react/forbid-prop-types
  sortColumnParameter: PropTypes.string,
  sortDirectionParameter: PropTypes.string,
  onQueryChange: PropTypes.func,
  emptyLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.shape({
    defaultMessage: PropTypes.string
  })]),
  withoutId: PropTypes.bool,
  withFadedId: PropTypes.bool,
  displayPlaceholder: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  selectable: PropTypes.bool,
  selectedItems: PropTypes.oneOf([PropTypes$1.items, PropTypes$1.item]),
  onSelectionChange: PropTypes.func,
  multipleSelection: PropTypes.bool,
  withCustomActionsColumn: PropTypes.bool,
  withoutLoading: PropTypes.bool,
  withoutEmpty: PropTypes.bool,
  actionsComponent: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  actionsProps: PropTypes.shape({}),
  actionsClassName: PropTypes.string,
  className: PropTypes.string
};
var defaultProps = {
  items: [],
  columns: [],
  loading: false,
  loaded: false,
  empty: false,
  theme: null,
  baseUrl: null,
  query: null,
  sortColumnParameter: 'order',
  sortDirectionParameter: 'order_direction',
  onQueryChange: null,
  emptyLabel: null,
  withoutId: false,
  withFadedId: true,
  displayPlaceholder: null,
  selectable: false,
  selectedItems: null,
  onSelectionChange: null,
  multipleSelection: false,
  withCustomActionsColumn: false,
  withoutLoading: false,
  withoutEmpty: false,
  actionsComponent: null,
  actionsProps: null,
  actionsClassName: null,
  className: null
};
function Table(_ref) {
  var items = _ref.items,
    columns = _ref.columns,
    loading = _ref.loading,
    loaded = _ref.loaded,
    empty = _ref.empty,
    theme = _ref.theme,
    baseUrl = _ref.baseUrl,
    query = _ref.query,
    sortColumnParameter = _ref.sortColumnParameter,
    sortDirectionParameter = _ref.sortDirectionParameter,
    onQueryChange = _ref.onQueryChange,
    emptyLabel = _ref.emptyLabel,
    withoutId = _ref.withoutId,
    withFadedId = _ref.withFadedId,
    displayPlaceholder = _ref.displayPlaceholder,
    selectable = _ref.selectable,
    selectedItems = _ref.selectedItems,
    onSelectionChange = _ref.onSelectionChange,
    multipleSelection = _ref.multipleSelection,
    withCustomActionsColumn = _ref.withCustomActionsColumn,
    withoutLoading = _ref.withoutLoading,
    withoutEmpty = _ref.withoutEmpty,
    actionsComponent = _ref.actionsComponent,
    actionsProps = _ref.actionsProps,
    actionsClassName = _ref.actionsClassName,
    className = _ref.className;
  var displayComponents = useDisplaysComponents();
  var hasIdColumn = (columns.find(function (_ref2) {
    var id = _ref2.id,
      field = _ref2.field;
    return id === 'id' || field === 'id';
  }) || null) !== null;
  var Actions = actionsComponent || null;
  var withActionsColumn = withCustomActionsColumn && Actions !== null;
  var withIdColumn = !withoutId && !hasIdColumn && !selectable;
  var finalSelectedItems = useMemo(function () {
    if (selectedItems === null) {
      return null;
    }
    return isArray(selectedItems) ? selectedItems : [selectedItems];
  }, [selectedItems]);
  var onSelectItem = useCallback(function () {
    var newItem = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    selectItem(newItem, selectedItems, onSelectionChange, multipleSelection);
  }, [items, selectedItems, onSelectionChange, multipleSelection]);
  var onSelectPage = useCallback(function () {
    var pageSelected = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    selectPage(pageSelected, items, selectedItems, onSelectionChange);
  }, [items, selectedItems, onSelectionChange]);
  var pageSelected = useMemo(function () {
    if (items === null || items.length === 0 || selectedItems === null || selectedItems.length === 0 || !multipleSelection) {
      return false;
    }
    var ids = (items || []).map(function () {
      var _ref3 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref3$id = _ref3.id,
        id = _ref3$id === void 0 ? null : _ref3$id;
      return id;
    }).filter(function (id) {
      return id !== null;
    }) || [];
    if (ids === null || ids.length === 0) {
      return false;
    }
    var currentPageItems = (selectedItems || []).filter(function (it) {
      var _ref4 = it || {},
        _ref4$id = _ref4.id,
        id = _ref4$id === void 0 ? null : _ref4$id;
      return (ids || []).indexOf(id) !== -1;
    }) || [];
    return currentPageItems.length > 0 && currentPageItems.length === (items || []).length;
  }, [selectedItems, items]);
  return /*#__PURE__*/React.createElement("div", null, items !== null ? /*#__PURE__*/React.createElement("table", {
    className: classNames(['table', 'table-sm', 'table-hover', 'align-middle', 'mb-0', _defineProperty(_defineProperty({}, "table-".concat(theme), theme !== null), className, className !== null)])
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, selectable && multipleSelection ? /*#__PURE__*/React.createElement("th", {
    scope: "col"
  }, /*#__PURE__*/React.createElement("input", {
    id: "checkAll",
    type: "checkbox",
    className: "form-check-input me-2",
    autoComplete: "off",
    checked: pageSelected,
    onChange: function onChange() {
      return onSelectPage(pageSelected);
    }
  })) : null, selectable && !multipleSelection ? /*#__PURE__*/React.createElement("th", {
    scope: "col"
  }, /*#__PURE__*/React.createElement("span", {
    className: "form-check-label pe-2 text-muted"
  })) : null, withIdColumn ? /*#__PURE__*/React.createElement("th", {
    scope: "col"
  }, "#") : null, columns.map(function (_ref6, idx) {
    var id = _ref6.id,
      _ref6$field = _ref6.field,
      field = _ref6$field === void 0 ? null : _ref6$field,
      _ref6$label = _ref6.label,
      label = _ref6$label === void 0 ? null : _ref6$label,
      _ref6$path = _ref6.path,
      path = _ref6$path === void 0 ? null : _ref6$path,
      _ref6$sortable = _ref6.sortable,
      columnSortable = _ref6$sortable === void 0 ? false : _ref6$sortable,
      _ref6$sortColumnName = _ref6.sortColumnName,
      sortColumnName = _ref6$sortColumnName === void 0 ? null : _ref6$sortColumnName,
      columnSortColumnParameter = _ref6.sortColumnParameter,
      columnSortDirectionParameter = _ref6.sortDirectionParameter,
      sortDirections = _ref6.sortDirections;
    return /*#__PURE__*/React.createElement("th", {
      scope: "col",
      key: "col-".concat(id, "-").concat(label, "-").concat(idx + 1)
    }, columnSortable ? /*#__PURE__*/React.createElement(SortLink, {
      className: "text-nowrap",
      baseUrl: baseUrl,
      query: query,
      field: sortColumnName || field || path,
      parameterName: columnSortColumnParameter || sortColumnParameter,
      directionParameterName: columnSortDirectionParameter || sortDirectionParameter,
      directions: sortDirections,
      onQueryChange: onQueryChange
    }, label) : /*#__PURE__*/React.createElement("span", {
      className: "text-nowrap"
    }, label));
  }), withActionsColumn ? /*#__PURE__*/React.createElement("th", {
    scope: "col"
  }, "\xA0") : null)), /*#__PURE__*/React.createElement("tbody", null, items.map(function (it, rowIdx) {
    var _ref7 = it || {},
      _ref7$id = _ref7.id,
      id = _ref7$id === void 0 ? null : _ref7$id,
      _ref7$rowClassName = _ref7.rowClassName,
      rowClassName = _ref7$rowClassName === void 0 ? null : _ref7$rowClassName,
      _ref7$actionsDisabled = _ref7.actionsDisabled,
      actionsDisabled = _ref7$actionsDisabled === void 0 ? false : _ref7$actionsDisabled,
      _ref7$selectionDisabl = _ref7.selectionDisabled,
      selectionDisabled = _ref7$selectionDisabl === void 0 ? false : _ref7$selectionDisabl,
      _ref7$loading = _ref7.loading,
      itemLoading = _ref7$loading === void 0 ? false : _ref7$loading;
    var checked = selectable && !selectionDisabled ? ((finalSelectedItems || []).find(function () {
      var _ref8 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
        _ref8$id = _ref8.id,
        itemId = _ref8$id === void 0 ? null : _ref8$id;
      return id === itemId;
    }) || null) !== null : false;

    // TODO: fix this
    var selectRow = function selectRow(e) {
      // if (onSelectItem !== null && checkClickable(e.target)) {
      //     onSelectItem(it);
      // }
      if (onSelectItem !== null && !selectionDisabled && e.target.tagName.toLowerCase() !== 'button' && e.target.tagName.toLowerCase() !== 'a' && e.target.tagName.toLowerCase() !== 'i') {
        onSelectItem(it);
      }
    };
    return /*#__PURE__*/React.createElement("tr", Object.assign({
      key: "row-".concat(id, "-").concat(rowIdx + 1),
      className: classNames([_defineProperty({
        'table-row': true
      }, rowClassName, rowClassName !== null)]),
      style: {
        // color: 'blue',
        borderColor: checked ? 'var(--bs-primary)' : null
      }
    }, onSelectionChange !== null ? {
      onClick: selectRow,
      role: 'button'
    } : null), selectable ? /*#__PURE__*/React.createElement("td", {
      className: "col-auto text-nowrap"
    }, /*#__PURE__*/React.createElement("input", {
      id: "check-".concat(id),
      type: "checkbox",
      className: "form-check-input",
      autoComplete: "off",
      checked: checked,
      onChange: selectRow
    }), /*#__PURE__*/React.createElement("span", {
      className: "form-check-label px-2 text-nowrap"
      // htmlFor={`check-${id}`}
    }, !withoutId && !hasIdColumn ? id : /*#__PURE__*/React.createElement(FormattedMessage, {
      id: "0SEHSj",
      defaultMessage: [{
        "type": 0,
        "value": "Select row"
      }]
    }))) : null, withIdColumn ? /*#__PURE__*/React.createElement("td", {
      className: "col-auto"
    }, /*#__PURE__*/React.createElement("span", {
      className: classNames([{
        'opacity-50': withFadedId
      }])
    }, id)) : null, columns.map(function (column, idx) {
      var _ref10 = column || {},
        colId = _ref10.id,
        component = _ref10.component,
        _ref10$field = _ref10.field,
        field = _ref10$field === void 0 ? null : _ref10$field,
        _ref10$path = _ref10.path,
        path = _ref10$path === void 0 ? null : _ref10$path,
        _ref10$columnClassNam = _ref10.columnClassName,
        columnClassName = _ref10$columnClassNam === void 0 ? null : _ref10$columnClassNam,
        displayProps = _objectWithoutProperties(_ref10, _excluded);
      var FieldDisplayComponent = getComponentFromName(colId === 'actions' ? component || 'actions' : component || 'text', displayComponents, colId === 'actions' && actionsComponent !== null ? actionsComponent : 'span');
      var displayValue = null;
      if (path !== null) {
        displayValue = get(it, path, null);
      } else if (field !== null) {
        displayValue = get(it, field.name, null);
      }
      return /*#__PURE__*/React.createElement("td", {
        key: "col-".concat(id, "-").concat(colId, "-").concat(idx + 1),
        className: classNames(['col-auto', _defineProperty({
          'text-break': displayValue !== null && isString(displayValue) && displayValue.length >= 30 || isObject(displayValue),
          'text-end': colId === 'actions' && !withActionsColumn
        }, columnClassName, columnClassName !== null)])
      }, FieldDisplayComponent !== null ? /*#__PURE__*/React.createElement(FieldDisplayComponent, Object.assign({}, displayProps, {
        actionsProps: colId === 'actions' ? actionsProps : null,
        field: field,
        value: displayValue,
        placeholder: itemLoading ? /*#__PURE__*/React.createElement("div", {
          className: "placeholder-glow"
        }, /*#__PURE__*/React.createElement("span", {
          className: "placeholder placeholder-xs w-100"
        })) : displayPlaceholder,
        selected: checked,
        item: it
      }, colId === 'actions' ? {
        disabled: actionsDisabled
      } : null)) : displayPlaceholder);
    }), withActionsColumn ? /*#__PURE__*/React.createElement("td", {
      className: classNames(['col-auto', _defineProperty({
        'table-row': true,
        'text-end': true
      }, actionsClassName, actionsClassName !== null)]),
      key: "col-".concat(id, "-actions")
    }, /*#__PURE__*/React.createElement(Actions, Object.assign({}, actionsProps, {
      item: it,
      disabled: actionsDisabled
    }))) : null);
  }))) : null, loading && !loaded && (items === null || items.length === 0) && !withoutLoading ? /*#__PURE__*/React.createElement(Loading, {
    className: "mt-3",
    withDelay: true
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "TV3jir",
    defaultMessage: [{
      "type": 0,
      "value": "Loading"
    }]
  })) : null, (empty || !loading && loaded && (items === null || items.length === 0)) && !withoutEmpty ? /*#__PURE__*/React.createElement(Empty, {
    className: "mt-3",
    withDelay: true
  }, emptyLabel || /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "AJiMQk",
    defaultMessage: [{
      "type": 0,
      "value": "No results"
    }]
  })) : null);
}
Table.propTypes = propTypes;
Table.defaultProps = defaultProps;

export { Table as default };
